import { useState, useEffect } from 'react';

interface WhiteLabelBranding {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string;
  faviconUrl?: string;
  fontFamily: string;
  customCss?: string;
}

interface WhiteLabelTenant {
  id: string;
  companyName: string;
  tagline?: string;
  contactEmail?: string;
  contactPhone?: string;
  licenseNumber?: string;
  licenseState?: string;
  description?: string;
}

interface WhiteLabelData {
  isWhiteLabel: boolean;
  domain: string;
  tenant?: WhiteLabelTenant;
  branding?: WhiteLabelBranding;
  features?: {
    customDomain: string;
    whiteLabel: boolean;
    hideKairosIQBranding: boolean;
  };
  status?: string;
}

/**
 * Hook to detect and apply white-label branding
 * Fetches tenant information from the backend and applies custom CSS
 */
export const useWhiteLabelBranding = () => {
  const [whiteLabelData, setWhiteLabelData] = useState<WhiteLabelData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWhiteLabelInfo = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Only check if not on main KairosIQ domain
        const hostname = window.location.hostname;
        if (hostname.includes('kairosiq.ai') || 
            hostname === 'localhost' || 
            hostname === '127.0.0.1') {
          setWhiteLabelData({ isWhiteLabel: false, domain: hostname });
          setIsLoading(false);
          return;
        }

        console.log('🔍 [White-Label] Checking for white-label branding on domain:', hostname);

        const response = await fetch('/api/white-label/tenant-info', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch tenant info: ${response.status}`);
        }

        const data: WhiteLabelData = await response.json();
        setWhiteLabelData(data);

        if (data.isWhiteLabel && data.branding) {
          console.log('✅ [White-Label] Applying branding for:', data.tenant?.companyName);
          applyBranding(data.branding, data.tenant);
          
          // Update document title if we have tenant info
          if (data.tenant?.companyName) {
            document.title = `${data.tenant.companyName} - Real Estate Search`;
          }
        } else {
          console.log('ℹ️ [White-Label] No white-label branding found for domain:', hostname);
        }

      } catch (err) {
        console.error('❌ [White-Label] Error fetching tenant info:', err);
        setError(err instanceof Error ? err.message : 'Failed to load branding');
        setWhiteLabelData({ isWhiteLabel: false, domain: window.location.hostname });
      } finally {
        setIsLoading(false);
      }
    };

    fetchWhiteLabelInfo();
  }, []);

  return {
    whiteLabelData,
    isWhiteLabel: whiteLabelData?.isWhiteLabel || false,
    branding: whiteLabelData?.branding,
    tenant: whiteLabelData?.tenant,
    isLoading,
    error
  };
};

/**
 * Apply white-label branding to the document
 */
const applyBranding = (branding: WhiteLabelBranding, tenant?: WhiteLabelTenant) => {
  const root = document.documentElement;

  // Apply CSS custom properties
  if (branding.primaryColor) {
    root.style.setProperty('--bs-primary', branding.primaryColor);
    root.style.setProperty('--white-label-primary', branding.primaryColor);
    console.log('🎨 [White-Label] Applied primary color:', branding.primaryColor);
  }

  if (branding.secondaryColor) {
    root.style.setProperty('--bs-secondary', branding.secondaryColor);
    root.style.setProperty('--white-label-secondary', branding.secondaryColor);
    console.log('🎨 [White-Label] Applied secondary color:', branding.secondaryColor);
  }

  if (branding.fontFamily) {
    root.style.setProperty('--white-label-font-family', branding.fontFamily);
    console.log('🎨 [White-Label] Applied font family:', branding.fontFamily);
  }

  // Update favicon if provided
  if (branding.faviconUrl) {
    updateFavicon(branding.faviconUrl);
  }

  // Apply custom CSS if provided
  if (branding.customCss) {
    injectCustomCSS(branding.customCss);
  }

  // Add branding class to body for additional styling hooks
  document.body.classList.add('white-label-theme');
  if (tenant?.companyName) {
    const slugified = tenant.companyName.toLowerCase().replace(/[^a-z0-9]/g, '-');
    document.body.classList.add(`tenant-${slugified}`);
  }

  console.log('✅ [White-Label] Branding applied successfully');
};

/**
 * Update the page favicon
 */
const updateFavicon = (faviconUrl: string) => {
  try {
    const existingFavicon = document.querySelector('link[rel="icon"]') || 
                          document.querySelector('link[rel="shortcut icon"]');
    
    if (existingFavicon) {
      existingFavicon.setAttribute('href', faviconUrl);
    } else {
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.href = faviconUrl;
      document.head.appendChild(favicon);
    }
    
    console.log('🎨 [White-Label] Updated favicon:', faviconUrl);
  } catch (error) {
    console.warn('⚠️ [White-Label] Failed to update favicon:', error);
  }
};

/**
 * Inject custom CSS into the document
 */
const injectCustomCSS = (customCss: string) => {
  try {
    const styleId = 'white-label-custom-css';
    let existingStyle = document.getElementById(styleId);
    
    if (existingStyle) {
      existingStyle.textContent = customCss;
    } else {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = customCss;
      document.head.appendChild(style);
    }
    
    console.log('🎨 [White-Label] Injected custom CSS');
  } catch (error) {
    console.warn('⚠️ [White-Label] Failed to inject custom CSS:', error);
  }
};

export default useWhiteLabelBranding;